import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckStatusBadge } from "@/components/Badges";
import { track } from "@/lib/analytics";

const OPTIONS = ["pass", "fail", "na", "untested"];

export default function ChecklistTab({ siteId }) {
  const [data, setData] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(() => {
    api.get(`/sites/${siteId}/checklist`).then((r) => setData(r.data)).catch(() => toast.error("Could not load checklist"));
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const update = async (criterionId, status, notes) => {
    try {
      await api.put(`/sites/${siteId}/checklist/${criterionId}`, { status, notes });
      track("checklist_updated", { criterion: criterionId, status });
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  if (!data) return <p className="text-sm text-ink-muted font-mono">Loading...</p>;

  const done = data.items.filter((i) => i.status !== "untested").length;

  return (
    <div data-testid="checklist-panel">
      <p className="text-sm text-ink-muted mb-4">
        Criteria that automated checks cannot evaluate. <span className="font-mono">{done}/{data.items.length}</span> reviewed.
      </p>
      <div className="table-card" tabIndex={0} role="region" aria-label="Manual WCAG checklist">
        <table className="w-full min-w-[840px] text-left border-collapse text-sm" data-testid="checklist-table">
          <caption className="sr-only">Manual WCAG checklist</caption>
          <thead>
            <tr className="border-b border-gray-300">
              <th scope="col" className="px-4 py-2.5 font-heading font-semibold">Criterion</th>
              <th scope="col" className="px-4 py-2.5 font-heading font-semibold">What to verify</th>
              <th scope="col" className="px-4 py-2.5 font-heading font-semibold">Result</th>
              <th scope="col" className="px-4 py-2.5 font-heading font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.criterion_id} className="border-b border-gray-200 last:border-0 align-top" data-testid={`checklist-row-${item.criterion_id}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <p className="font-mono text-xs font-medium">{item.criterion_id} <span className="text-ink-subtle">({item.level})</span></p>
                  <p className="text-xs mt-1 max-w-[160px] whitespace-normal">{item.name}</p>
                  {item.version === "2.2" && <span className="font-mono text-[10px] text-[#16324F]">WCAG 2.2</span>}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted max-w-[320px]">{item.guidance}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <label htmlFor={`check-${item.criterion_id}`} className="sr-only">Result for {item.criterion_id}</label>
                    <select
                      id={`check-${item.criterion_id}`}
                      data-testid={`checklist-status-${item.criterion_id}`}
                      value={item.status}
                      onChange={(e) => update(item.criterion_id, e.target.value, notesDraft[item.criterion_id])}
                      className="border border-gray-300 rounded-sm px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-[#16324F] outline-none"
                    >
                      {OPTIONS.map((o) => <option key={o} value={o}>{o === "na" ? "N/A" : o}</option>)}
                    </select>
                    <CheckStatusBadge status={item.status} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <label htmlFor={`notes-${item.criterion_id}`} className="sr-only">Notes for {item.criterion_id}</label>
                  <textarea
                    id={`notes-${item.criterion_id}`}
                    data-testid={`checklist-notes-${item.criterion_id}`}
                    rows={1}
                    defaultValue={item.notes}
                    onChange={(e) => setNotesDraft({ ...notesDraft, [item.criterion_id]: e.target.value })}
                    onBlur={(e) => { if (e.target.value !== item.notes) update(item.criterion_id, item.status, e.target.value); }}
                    className="w-full min-w-[180px] border border-gray-300 rounded-sm px-2 py-1 text-xs focus:ring-2 focus:ring-[#16324F] outline-none"
                    placeholder="Evidence, scope, notes"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
